import {
  type Goal,
  type GoalRequest,
  goalRequestSchema,
  isNegativeMoney,
  parseAmountInput,
  setBudgetRequestSchema,
  setLimitRequestSchema,
} from '@familywallet/shared';
import { z } from 'zod';
import { parsePositive } from './add-form';
import { groupAmountInput } from './money';

/** A monthly amount typed by a person ("250", "1.5k"): zero or more, in `currency`. */
export function parseMonthlyAmount(raw: string, currency: string): string | null {
  const amount = parseAmountInput(raw, currency);
  return amount !== null && !isNegativeMoney(amount) ? amount : null;
}

export interface BudgetFormValues {
  categoryId: string;
  amount: string;
}

/** The budget form: "overall" or a category, and an amount; checked by the API's schema. */
export function budgetFormSchema(currency: string) {
  return z
    .custom<BudgetFormValues>()
    .superRefine((v, c) => {
      if (v.categoryId === '') {
        c.addIssue({ code: 'custom', path: ['categoryId'], message: 'budgets.errors.category' });
      }
      if (parseMonthlyAmount(v.amount, currency) === null) {
        c.addIssue({ code: 'custom', path: ['amount'], message: 'budgets.errors.amount' });
      }
    })
    .transform((v) =>
      setBudgetRequestSchema.parse({
        categoryId: v.categoryId === 'overall' ? null : v.categoryId,
        amount: parseMonthlyAmount(v.amount, currency),
      }),
    );
}

/** The limit form (one amount); checked by the API's schema. */
export function limitFormSchema(currency: string) {
  return z
    .custom<{ amount: string }>()
    .superRefine((v, c) => {
      if (parseMonthlyAmount(v.amount, currency) === null) {
        c.addIssue({ code: 'custom', path: ['amount'], message: 'budgets.errors.amount' });
      }
    })
    .transform((v) =>
      setLimitRequestSchema.parse({ amount: parseMonthlyAmount(v.amount, currency) }),
    );
}

/** What the goal form holds, as typed. */
export interface GoalFormValues {
  name: string;
  targetAmount: string;
  currency: string;
  targetDate: string;
  accountId: string;
  familyWide: boolean;
}

const DATE = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;

export function goalFormSchema() {
  return z
    .custom<GoalFormValues>()
    .superRefine((v, c) => {
      if (v.name.trim() === '') {
        c.addIssue({ code: 'custom', path: ['name'], message: 'goals.errors.name' });
      }
      if (!parsePositive(v.targetAmount, v.currency)) {
        c.addIssue({
          code: 'custom',
          path: ['targetAmount'],
          message:
            v.currency === 'LBP' && parsePositive(v.targetAmount, 'USD')
              ? 'add.errors.amountWhole'
              : 'add.errors.amount',
        });
      }
      if (v.accountId === '') {
        c.addIssue({ code: 'custom', path: ['accountId'], message: 'goals.errors.account' });
      }
      if (v.targetDate !== '' && !DATE.test(v.targetDate)) {
        c.addIssue({ code: 'custom', path: ['targetDate'], message: 'goals.errors.date' });
      }
    })
    .transform(
      (v) =>
        goalRequestSchema.parse({
          name: v.name,
          targetAmount: parsePositive(v.targetAmount, v.currency),
          currency: v.currency,
          targetDate: v.targetDate === '' ? null : v.targetDate,
          accountId: v.accountId,
          familyWide: v.familyWide,
        }) as GoalRequest,
    );
}

export function goalFormValues(
  goal: Goal | undefined,
  fallback: { accountId: string; currency: string },
): GoalFormValues {
  if (!goal) {
    return {
      name: '',
      targetAmount: '',
      currency: fallback.currency,
      targetDate: '',
      accountId: fallback.accountId,
      familyWide: false,
    };
  }
  return {
    name: goal.name,
    targetAmount: groupAmountInput(goal.targetAmount, goal.currency),
    currency: goal.currency,
    targetDate: goal.targetDate ?? '',
    accountId: goal.accountId,
    familyWide: goal.ownerMemberId === null,
  };
}
